/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import classNames from 'classnames';

import { Button, Typography2, VisuallyHidden } from '@coursera/cds-core';
import { breakpoints } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import TrackedDiv from 'bundles/page/components/TrackedDiv';
import SvgCompatibleImage from 'bundles/search-common/components/SvgCompatibleImage';
import type { Hits } from 'bundles/search-common/types/autocomplete';

import _t from 'i18n!nls/search';

const styles = {
  onboardingSuggestion: css`
    padding: 0 var(--cds-spacing-300);

    ${breakpoints.down('sm')} {
      margin-left: calc(var(--cds-spacing-300) * -1);
      margin-right: calc(var(--cds-spacing-300) * -1);
    }

    .react-autosuggest__suggestion--highlighted & {
      background-color: #f3f8ff;
    }
  `,
  onboardingSuggestionButton: css`
    margin-bottom: 2px;

    &:hover {
      background-color: #f3f8ff;
      text-decoration: none;
    }
  `,
};

export const AutoCompleteSuggestion = (hit: Hits) => {
  const isProductDirectMatch = hit.indexName === 'Search_ProductHit';
  const directMatchClassName = classNames('section-item', 'horizontal-box', 'align-items-vertical-center', {
    'direct-match-section': isProductDirectMatch,
  });
  const partnerLabel = hit.partners?.[0] ? `, ${hit.partners[0]}` : '';
  let ariaLabel = `${hit.name}${partnerLabel}.`;
  if (hit.sectionTitle) ariaLabel = `${hit.sectionTitle}: ${hit.name}${partnerLabel}.`;
  // manually setting the aria-label in the wrapper div to make sure it's read out correctly, hide the texts
  // Note: this is slightly broken due to double announcement because the Autosuggest library also announce the value selected. If we remove the label here then when using VO modifier (ctrl+opt+shift) it wouldn't be announced at all.

  if (hit.__typename === 'Search_RedirectHit') {
    ariaLabel = `${hit.name} ${_t('Take a short quiz')}`;
    return (
      <TrackedDiv
        trackingName="autocomplete_item"
        data={{
          name: hit.name,
          objectID: hit.objectID,
        }}
        withVisibilityTracking={true}
        aria-label={ariaLabel}
        css={styles.onboardingSuggestion}
      >
        <Typography2 component="p" variant="bodyPrimary">
          {hit.name}
          <Button css={styles.onboardingSuggestionButton} icon={<ArrowNextIcon />} variant="ghost" size="small">
            {_t('Take a short quiz')}
          </Button>
        </Typography2>
      </TrackedDiv>
    );
  }
  return (
    <TrackedDiv
      trackingName="autocomplete_item"
      data={{
        name: hit.name,
        objectID: hit.objectID,
      }}
      withVisibilityTracking={true}
      className={directMatchClassName}
      aria-label={ariaLabel}
    >
      {hit.image?.imageUrl && (
        <div className="image-container">
          <SvgCompatibleImage
            className="item-photo"
            src={hit.image.imageUrl}
            alt={_t('Suggestion Image')}
            imgParams={{ fit: 'fill', auto: 'format,compress' }}
            width={hit.image.size}
            height={hit.image.size}
            aria-hidden={true}
          />
        </div>
      )}
      {!hit.image?.imageUrl && <div style={{ width: 32 }} />}
      <div className="vertical-box">
        {hit.sectionTitle ? (
          <VisuallyHidden>
            <span className="item-name body-1-text" aria-hidden={true}>{`${hit.sectionTitle}: `}</span>
          </VisuallyHidden>
        ) : (
          ''
        )}
        <span className="item-name body-1-text" aria-hidden={true}>
          {hit.name}
        </span>
        {hit.partners?.[0] ? (
          <span className="partner-name" aria-hidden={true}>
            {hit.partners[0]}
          </span>
        ) : (
          ''
        )}
      </div>
    </TrackedDiv>
  );
};

export default AutoCompleteSuggestion;
